import moment from 'moment';
import axios from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import useUrlParams from './useUrlParams';
import { setDataLoad } from '../redux/slices/preloader';
import { requestLimit } from '../constants/configs';
import { DAY, MONTH } from '../constants';

const linkClicksRequestPage = 1;
const linksAggregationSize = 100;
const pagesAggregationSize = 1000;
const topValuesAggregationSize = 10;
const minuteSlotSize = 5;
const hoursInDay = 24;
const minutesInHour = 60;

// Elastic stores raw detector values (smartphone, phablet, tv, ...), while the
// dashboard only renders mobile, desktop and tablet.
const DEVICE_GROUPS = {
  smartphone: 'mobile',
  mobile: 'mobile',
  phablet: 'mobile',
  'feature phone': 'mobile',
  desktop: 'desktop',
  tablet: 'tablet'
};

const normalizeDevice = (device) =>
  DEVICE_GROUPS[device?.toLowerCase()] ?? 'other';

const getShare = (value, total) =>
  total ? Number(((value / total) * 100).toFixed(1)) : 0;

const getTotalHits = (hits) => {
  const total = hits?.total;
  if (typeof total === 'number') return total;
  return total?.value ?? 0;
};

const getCtr = (clicks, views) =>
  views ? Math.min(getShare(clicks, views), 100) : null;

const getTopBucketKey = (aggregation) => aggregation?.buckets?.[0]?.key ?? null;

const getPageId = (bucket) => {
  const pageId = bucket.page_id?.value;
  return pageId === undefined || pageId === null ? null : pageId;
};

const mapScansByPage = (buckets) =>
  new Map(buckets.map((bucket) => [String(bucket.key), bucket.doc_count]));

const mapTrend = (buckets) =>
  buckets.map((bucket) => ({
    date: bucket.key_as_string,
    total: bucket.doc_count,
    unique: bucket.unique_clicks.value
  }));

const mapCountries = (aggregation, totalClicks) => ({
  items: aggregation.buckets.map((bucket) => ({
    country: bucket.key,
    clicks: bucket.doc_count,
    share: getShare(bucket.doc_count, totalClicks)
  })),
  otherClicks: aggregation.sum_other_doc_count
});

const mapDevices = (buckets, totalClicks) => {
  const clicksByGroup = buckets.reduce((groups, bucket) => {
    const device = normalizeDevice(bucket.key);
    groups[device] = (groups[device] ?? 0) + bucket.doc_count;
    return groups;
  }, {});

  return Object.entries(clicksByGroup)
    .map(([device, clicks]) => ({
      device,
      clicks,
      share: getShare(clicks, totalClicks)
    }))
    .sort((first, second) => second.clicks - first.clicks);
};

const mapLinks = (buckets, totalViewsByPage, pageTitles) =>
  buckets.map((bucket) => {
    const url = getTopBucketKey(bucket.target_url);
    const pageId = getPageId(bucket);
    // Views of other pages must not leak into a link's CTR, so an unresolved
    // page leaves the column empty instead of borrowing the overall total.
    const pageViews =
      pageId === null ? null : totalViewsByPage.get(String(pageId));
    const uniqueClicks = bucket.unique_clicks.value;
    return {
      linkId: bucket.key,
      label: getTopBucketKey(bucket.link_label) || url,
      url,
      totalClicks: bucket.doc_count,
      uniqueClicks,
      ctr: getCtr(uniqueClicks, pageViews),
      // Page titles are resolved by the API from MySQL and returned separately
      // from the Elasticsearch document payload.
      pageTitle: pageId === null ? null : pageTitles[String(pageId)] ?? null,
      topCountry: getTopBucketKey(bucket.top_country),
      topDevice: normalizeDevice(getTopBucketKey(bucket.top_device)),
      lastClickAt: bucket.last_click.value_as_string ?? null
    };
  });

// Elastic returns one bucket per hour of the period, the widget needs one
// bucket per hour of the day, so buckets are folded into fixed slots.
const foldIntoDaySlots = (buckets, slotCount, getSlotIndex) => {
  const slots = new Array(slotCount).fill(0);
  buckets.forEach((bucket) => {
    slots[getSlotIndex(moment.parseZone(bucket.key_as_string))] +=
      bucket.doc_count;
  });
  return slots;
};

const mapClicksByHour = (buckets) =>
  foldIntoDaySlots(buckets, hoursInDay, (date) => date.hour()).map(
    (clicks, hour) => ({ hour, clicks })
  );

const mapClicksByMinute = (buckets) =>
  foldIntoDaySlots(buckets, minutesInHour / minuteSlotSize, (date) =>
    Math.floor(date.minute() / minuteSlotSize)
  ).map((clicks, index) => ({ minute: index * minuteSlotSize, clicks }));

const emptyLinkClicks = () => ({
  summary: { totalClicks: 0, uniqueVisitors: 0, ctr: null },
  trend: { [DAY]: [], [MONTH]: [] },
  daytime: { hour: [], minute: [] },
  byCountry: { items: [], otherClicks: 0 },
  byDevice: [],
  links: []
});

const useLinkClicksRequest = () => {
  const { location, device, qrIds, periodStart, periodEnd } = useSelector(
    (state) => ({
      location: state.filters.location,
      device: state.filters.device,
      qrIds: state.filters.qrIds,
      periodStart: state.filters.periodStart,
      periodEnd: state.filters.periodEnd
    })
  );

  const { baseUrl, id } = useUrlParams();
  const dispatch = useDispatch();

  const BASE_URL = `${baseUrl}/statistic/link-clicks/request?page=${linkClicksRequestPage}&limit=${requestLimit}`;
  // Page views stayed in the scan index behind the old endpoint, and CTR needs
  // them both overall and per page.
  const SCANS_URL = `${baseUrl}/statistic/request?page=${linkClicksRequestPage}&limit=${requestLimit}`;

  const getParams = () => {
    const query = [];
    if (location.length) {
      query.push({ match: { 'country.keyword': location } });
    }
    if (device.length) {
      query.push({ match: { 'device.keyword': device } });
    }
    if (qrIds.length) {
      query.push({
        terms: {
          'page-id': qrIds
        }
      });
    }
    return { query };
  };

  const getLinkClicks = async ({
    startDate = periodStart,
    endDate = periodEnd
  } = {}) => {
    const { query } = getParams();
    // Default redux periodEnd is captured at app init (moment()), so clicks made
    // later the same day fall outside lt unless we extend to end of day.
    const normalizedStartDate = moment(startDate).startOf('day').toDate();
    const normalizedEndDate = moment(endDate).endOf('day').toDate();
    const offsetMinutes = moment.parseZone(normalizedStartDate).utcOffset();
    const timeZoneOffset = moment()
      .utcOffset(offsetMinutes)
      .locale('en')
      .format('Z');
    const uniqueClicksAggregation = {
      cardinality: {
        field: 'click_hash.keyword'
      }
    };
    const topValueAggregation = (field) => ({
      terms: {
        field,
        size: 1
      }
    });
    const dateRange = {
      range: {
        'created-at-date': {
          gte: normalizedStartDate,
          lte: normalizedEndDate
        }
      }
    };

    const getPageViews = async () => {
      const viewsParams = {
        query: {
          bool: {
            filter: [dateRange, ...query]
          }
        },
        size: 0,
        track_total_hits: true
      };
      const requestViews = (searchParams) =>
        axios
          .post(SCANS_URL, { searchParams, id })
          .then(({ data: { documents } }) => documents);

      try {
        let views;
        try {
          views = await requestViews({
            ...viewsParams,
            aggs: {
              views_by_page: {
                terms: {
                  field: 'page-id',
                  size: pagesAggregationSize
                }
              }
            }
          });
        } catch (e) {
          // Losing the per-page split still leaves a usable overall CTR.
          views = await requestViews(viewsParams);
        }

        return {
          totalViews: getTotalHits(views.hits),
          totalViewsByPage: mapScansByPage(
            views.aggregations?.views_by_page?.buckets ?? []
          )
        };
      } catch (e) {
        return { totalViews: 0, totalViewsByPage: new Map() };
      }
    };

    try {
      dispatch(setDataLoad({ isGetLinkClicksLoad: true }));
      const [
        {
          data: { documents: data, pageTitles = {} }
        },
        { totalViews, totalViewsByPage }
      ] = await Promise.all([
        axios.post(BASE_URL, {
          searchParams: {
            query: {
              bool: {
                filter: [dateRange, ...query]
              }
            },
            size: 0,
            track_total_hits: true,
            aggs: {
              // click_hash is unique per visitor and link, so a visitor who
              // clicked several links has to be counted through visitor_hash.
              unique_visitors: {
                cardinality: {
                  field: 'visitor_hash.keyword'
                }
              },
              trend_by_day: {
                date_histogram: {
                  field: 'created-at-date',
                  calendar_interval: DAY,
                  min_doc_count: 0,
                  time_zone: timeZoneOffset
                },
                aggs: {
                  unique_clicks: uniqueClicksAggregation
                }
              },
              trend_by_month: {
                date_histogram: {
                  field: 'created-at-date',
                  calendar_interval: MONTH,
                  min_doc_count: 0,
                  time_zone: timeZoneOffset
                },
                aggs: {
                  unique_clicks: uniqueClicksAggregation
                }
              },
              // Empty buckets are skipped on purpose: the result is folded into
              // 24 hour and 12 minute slots, and a long period with zero-filled
              // buckets would blow past the Elastic bucket limit.
              clicks_by_hour: {
                date_histogram: {
                  field: 'created-at-date',
                  fixed_interval: '1h',
                  min_doc_count: 1,
                  time_zone: timeZoneOffset
                }
              },
              clicks_by_minute: {
                date_histogram: {
                  field: 'created-at-date',
                  fixed_interval: `${minuteSlotSize}m`,
                  min_doc_count: 1,
                  time_zone: timeZoneOffset
                }
              },
              clicks_by_country: {
                terms: {
                  field: 'country.keyword',
                  order: {
                    _count: 'desc'
                  },
                  size: topValuesAggregationSize
                }
              },
              clicks_by_device: {
                terms: {
                  field: 'device.keyword',
                  order: {
                    _count: 'desc'
                  },
                  size: topValuesAggregationSize
                }
              },
              links: {
                terms: {
                  field: 'link_id.keyword',
                  order: {
                    _count: 'desc'
                  },
                  size: linksAggregationSize
                },
                aggs: {
                  target_url: topValueAggregation('target_url.keyword'),
                  link_label: topValueAggregation('link_label.keyword'),
                  page_id: {
                    max: {
                      field: 'page-id'
                    }
                  },
                  unique_clicks: uniqueClicksAggregation,
                  top_country: topValueAggregation('country.keyword'),
                  top_device: topValueAggregation('device.keyword'),
                  last_click: {
                    max: {
                      field: 'created-at-date'
                    }
                  }
                }
              }
            }
          },
          id
        }),
        getPageViews()
      ]);

      const totalClicks = getTotalHits(data?.hits);
      const aggregations = data?.aggregations;

      if (!aggregations) {
        console.error('Link clicks: invalid API response', data);
        return emptyLinkClicks();
      }

      return {
        summary: {
          totalClicks,
          uniqueVisitors: aggregations.unique_visitors.value,
          ctr: getCtr(aggregations.unique_visitors.value, totalViews)
        },
        trend: {
          [DAY]: mapTrend(aggregations.trend_by_day.buckets),
          [MONTH]: mapTrend(aggregations.trend_by_month.buckets)
        },
        daytime: {
          hour: mapClicksByHour(aggregations.clicks_by_hour.buckets),
          minute: mapClicksByMinute(aggregations.clicks_by_minute.buckets)
        },
        byCountry: mapCountries(aggregations.clicks_by_country, totalClicks),
        byDevice: mapDevices(aggregations.clicks_by_device.buckets, totalClicks),
        links: mapLinks(
          aggregations.links.buckets,
          totalViewsByPage,
          pageTitles
        )
      };
    } catch (e) {
      console.error('Link clicks request failed', e);
      return emptyLinkClicks();
    } finally {
      dispatch(setDataLoad({ isGetLinkClicksLoad: false }));
    }
  };

  return { getLinkClicks };
};

export default useLinkClicksRequest;
